import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// Speaker roles in diarized transcripts. Diarization writes "Speaker 0: ..."
// turns; once roles are known the label carries them in brackets, for
// example "Speaker 1 [Caregiver (mother)]: ...". The role lives in the
// stored transcript itself, so every reader (web, note prompts, exports)
// sees the same labels and no schema change is needed. "Unknown" marks a
// speaker that was checked but could not be identified.
export const SPEAKER_ROLES = [
  "clinician",
  "patient",
  "caregiver",
  "other_clinician",
  "interpreter",
  "other_patient",
  "other",
  "unknown",
] as const;
export const speakerRoleSchema = z.enum(SPEAKER_ROLES);
export type SpeakerRole = z.output<typeof speakerRoleSchema>;

// Relation of a caregiver to the patient, only when the conversation says it.
// A closed list, so a label can never carry a name.
export const CAREGIVER_RELATIONS = [
  "mother",
  "father",
  "parent",
  "guardian",
  "spouse",
  "partner",
  "wife",
  "husband",
  "son",
  "daughter",
  "child",
  "sister",
  "brother",
  "sibling",
  "grandmother",
  "grandfather",
  "grandparent",
  "family member",
  "friend",
] as const;
export const caregiverRelationSchema = z.enum(CAREGIVER_RELATIONS);
export type CaregiverRelation = z.output<typeof caregiverRelationSchema>;

export const SPEAKER_ROLE_LABELS: Record<SpeakerRole, string> = {
  clinician: "Clinician",
  patient: "Patient",
  caregiver: "Caregiver",
  other_clinician: "Other clinician",
  interpreter: "Interpreter",
  other_patient: "Other patient",
  other: "Other",
  unknown: "Unknown",
};

export type SpeakerAssignment = {
  role: SpeakerRole;
  relation?: CaregiverRelation | null;
};

const roleByLabel = new Map(
  Object.entries(SPEAKER_ROLE_LABELS).map(([role, label]) => [
    label.toLowerCase(),
    role as SpeakerRole,
  ]),
);
const relations = new Set<string>(CAREGIVER_RELATIONS);

// "Speaker 3" or "Speaker 3 [Caregiver (mother)]" followed by a colon, at the
// start of the text or after whitespace (realtime turns are joined by spaces).
const LABEL =
  /(^|\s)Speaker (\d{1,2})(?: \[([A-Za-z ]{1,24})(?: \(([A-Za-z ]{1,16})\))?\])?:(?=\s|$)/g;
const HAS_LABEL = new RegExp(LABEL.source);

export const speakerTag = (assignment: SpeakerAssignment): string => {
  const label = SPEAKER_ROLE_LABELS[assignment.role];
  return assignment.role === "caregiver" && assignment.relation
    ? `${label} (${assignment.relation})`
    : label;
};

const parseTag = (
  label: string | undefined,
  relation: string | undefined,
): SpeakerAssignment | null => {
  if (label === undefined) return null;
  const role = roleByLabel.get(label.toLowerCase());
  if (!role) return null;
  return role === "caregiver" &&
    relation &&
    relations.has(relation.toLowerCase())
    ? { role, relation: relation.toLowerCase() as CaregiverRelation }
    : { role };
};

export type SpeakerTurn = {
  // null: text before the first speaker label (or an undiarized transcript).
  speaker: number | null;
  assignment: SpeakerAssignment | null;
  text: string;
};

// Splits a transcript into speaker turns, merging consecutive turns of the
// same speaker. Text without labels is one turn with speaker null.
export const parseSpeakerTurns = (transcript: string): SpeakerTurn[] => {
  const turns: SpeakerTurn[] = [];
  let current: SpeakerTurn | null = null;
  let last = 0;
  const flush = (end: number) => {
    const text = transcript.slice(last, end).trim();
    if (current) current.text = [current.text, text].filter(Boolean).join(" ");
    else if (text) turns.push({ speaker: null, assignment: null, text });
  };
  for (const match of transcript.matchAll(LABEL)) {
    const start = (match.index ?? 0) + match[1].length;
    flush(start);
    last = start + match[0].length - match[1].length;
    const speaker = Number(match[2]);
    const assignment = parseTag(match[3], match[4]);
    if (current && current.speaker === speaker) {
      current.assignment = assignment ?? current.assignment;
      continue;
    }
    current = { speaker, assignment, text: "" };
    turns.push(current);
  }
  flush(transcript.length);
  return turns.filter((turn) => turn.speaker !== null || turn.text);
};

export type TranscriptSpeaker = {
  speaker: number;
  // null: diarized but not yet identified.
  assignment: SpeakerAssignment | null;
};

// The speakers of one transcript in order of first appearance, with the
// role their first label carries.
export const transcriptSpeakers = (transcript: string): TranscriptSpeaker[] => {
  const seen = new Map<number, TranscriptSpeaker>();
  for (const turn of parseSpeakerTurns(transcript))
    if (turn.speaker !== null && !seen.has(turn.speaker))
      seen.set(turn.speaker, {
        speaker: turn.speaker,
        assignment: turn.assignment,
      });
    else if (turn.speaker !== null && turn.assignment) {
      const entry = seen.get(turn.speaker);
      if (entry && !entry.assignment) entry.assignment = turn.assignment;
    }
  return [...seen.values()];
};

export const hasUnidentifiedSpeakers = (transcript: string): boolean =>
  transcriptSpeakers(transcript).some((speaker) => !speaker.assignment);

// Rewrites only the speaker labels. Speakers missing from the map keep their
// label; everything else in the transcript stays byte for byte.
export const applySpeakerRoles = (
  transcript: string,
  roles: ReadonlyMap<number, SpeakerAssignment>,
): string =>
  transcript.replace(LABEL, (whole, lead: string, speaker: string) => {
    const assignment = roles.get(Number(speaker));
    return assignment
      ? `${lead}Speaker ${speaker} [${speakerTag(assignment)}]:`
      : whole;
  });

// The transcript as one turn per line, for prompts: merged turns, labels in
// canonical form. Recordings joined with a line of asterisks stay separate.
export const formatSpeakerTranscript = (transcript: string): string =>
  transcript
    .split(/(\n\*{10,}\n?)/)
    .map((part, index) =>
      index % 2 === 1 || !HAS_LABEL.test(part)
        ? part
        : parseSpeakerTurns(part)
            .map((turn) =>
              turn.speaker === null
                ? turn.text
                : `Speaker ${turn.speaker}${turn.assignment ? ` [${speakerTag(turn.assignment)}]` : ""}: ${turn.text}`,
            )
            .join("\n"),
    )
    .join("");

const transcriptSpeakersSchema = z.object({
  // null: the visit's first recording; otherwise an additional recording.
  recordingPublicId: publicIdSchema.nullable(),
  speakers: z
    .array(
      z.object({
        speaker: z.int().min(0).max(99),
        role: speakerRoleSchema.nullable(),
        relation: caregiverRelationSchema.nullable(),
      }),
    )
    .max(100),
});
export const visitSpeakersSchema = z.object({
  transcripts: z.array(transcriptSpeakersSchema).max(67),
});
export type VisitSpeakers = z.output<typeof visitSpeakersSchema>;
export const visitSpeakerUpdateSchema = z.strictObject({
  recordingPublicId: publicIdSchema.nullable(),
  speaker: z.int().min(0).max(99),
  role: speakerRoleSchema,
  relation: caregiverRelationSchema.nullable().optional(),
});
export type VisitSpeakerUpdate = z.output<typeof visitSpeakerUpdateSchema>;

const speakers = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Not Found" },
  CONFLICT: { message: "Transcript changed" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Speaker roles unavailable" },
});
const meta = (method: "POST" | "PUT") =>
  openapi({
    method,
    path: "/api/v2/visits/{public_id}/speakers",
    inputStructure: "detailed",
    successStatus: 200,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
const query = z.strictObject({}).default({});
export const speakerRoleContract = {
  // Identifies the speakers of every diarized transcript of the visit that
  // still has unlabelled speakers and stores the roles in the transcript.
  // Repeating it is a no-op once every speaker has a role.
  identifyVisitSpeakers: speakers
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query,
        body: z.undefined().optional(),
      }),
    )
    .meta(meta("POST"))
    .output(visitSpeakersSchema),
  // The clinician sets one speaker's role.
  updateVisitSpeaker: speakers
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query,
        body: visitSpeakerUpdateSchema,
      }),
    )
    .meta(meta("PUT"))
    .output(visitSpeakersSchema),
};
